import {ref, watch} from 'vue'
import axios from 'axios'
import _ from 'lodash'

import {saveErrorMessages, toTemplate} from '@/components/GroupReports/settings/groupReportRows.js'

/**
 * Loads and saves one report's group reports (report templates) through the
 * existing api-internal report-templates endpoints. The list, the New / Edit
 * page and the Default Settings panel all go through here, so a template is
 * always found by id and every failure comes back as displayable messages.
 *
 * @param {string} reportName   The report's report_name, e.g. 'excursion-report'.
 * @param {function} apiRoute   (path) => URL, e.g. company.buildCompanyApiInternalRoute.
 */
export function useReportTemplates(reportName, apiRoute) {
    const templates = ref([])
    const defaultTemplate = ref(null)
    const groups = ref({})
    const usersByGroup = ref({})
    const loading = ref(false)
    const loaded = ref(false)
    const search = ref('')

    const load = () => {
        loading.value = true

        return axios.get(apiRoute('report-templates'), {params: {report_name: reportName, search: search.value}})
            .then(({data}) => {
                groups.value = data.groups ?? {}
                usersByGroup.value = data.usersByGroup ?? {}
                defaultTemplate.value = data.defaultTemplate ? toTemplate(data.defaultTemplate, groups.value) : null
                templates.value = (data.data ?? []).map((row) => toTemplate(row, groups.value))
                loaded.value = true
            })
            .finally(() => {
                loading.value = false
            })
    }

    watch(search, _.debounce(() => load().catch(() => {}), 250))

    const find = (id) => templates.value.find((template) => String(template.id) === String(id))

    const create = ({groupId, templateName, interval, settings}) => axios
        .post(apiRoute('report-templates'), {groupId, reportName, templateName, interval, settings})
        .then(({data}) => toTemplate(data, groups.value))

    const update = (id, {templateName, interval, settings}) => axios
        .put(apiRoute(`report-templates/${id}`), {group_report: true, template_name: templateName, interval, settings})
        .then(({data}) => toTemplate(data, groups.value))

    // The default template is the one with no group: it is addressed as
    // report-templates/null and found by report name on the server.
    const saveDefault = ({interval, settings}) => axios
        .put(apiRoute('report-templates/null'), {group_report: false, report_name: reportName, interval, settings})
        .then(({data}) => {
            defaultTemplate.value = toTemplate(data, groups.value)
        })

    const archive = (id) => axios
        .delete(apiRoute(`report-templates/${id}`))
        .then(() => {
            templates.value = templates.value.filter((template) => String(template.id) !== String(id))
        })

    return {
        templates,
        defaultTemplate,
        groups,
        usersByGroup,
        loading,
        loaded,
        search,
        load,
        find,
        create,
        update,
        saveDefault,
        archive,
        errorMessages: saveErrorMessages,
    }
}
